import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Truck, MapPin, Mail, Phone, Upload, FileText, X, CheckCircle2,
  Shield, Calendar, Award, Loader2, RefreshCw, Sparkles, Building2
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useToast } from '../hooks/use-toast';
import SourceSection from '../components/SourceSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLOWED_EXT = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
const ALLOWED_IMG_EXT = ['.jpg', '.jpeg', '.png', '.webp'];
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_PAPERWORK = 10;

const EQUIPMENT_TYPES = ['Semi Truck', 'Hotshot Truck', 'Pickup Truck', 'Car Hauler Truck'];
const TRAILER_TYPES = ['Flatbed', 'Car Hauler', 'Lowboy', 'Gooseneck', 'Enclosed Trailer'];
const APPLYING_AS = ['Owner Operator', 'Company Driver', 'Team Driver'];

const fmtBytes = (b) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

const SectionHead = ({ num, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-4">
    <div className="w-8 h-8 rounded-lg bg-[#0A1628] text-[#2AA8E8] font-extrabold flex items-center justify-center text-sm shrink-0">{num}</div>
    <div>
      <h3 className="text-lg font-extrabold navy-text">{title}</h3>
      {subtitle && <p className="text-sm text-[#4A5A72]">{subtitle}</p>}
    </div>
  </div>
);

const DriversPage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    firstName: '', lastName: '', phone: '', email: '', location: '',
    applyingAs: 'Owner Operator',
    authorityType: 'excelence', // 'excelence' | 'own'
    companyName: '', mcNumber: '', dotNumber: '',
    equipmentType: '',
    hasTrailer: 'no',
    trailerType: '', trailerYear: '', trailerLength: '', carsCapacity: '',
    experienceYears: '', cleanRecord: 'yes', startDate: '',
    // Source tracking
    source: '', referralName: '', referralPhone: '', otherSource: '',
  });
  const [files, setFiles] = useState([]);
  const [truckPhoto, setTruckPhoto] = useState(null);
  const [trailerPhoto, setTrailerPhoto] = useState(null);
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [hp, setHp] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = 'Drive With Us — Owner Operators & Drivers | Excelence Inc';
    const metaDesc = document.querySelector('meta[name="description"]');
    const content = 'Apply with Excelence Inc as an owner operator, company driver, or team driver. Work under our MC/DOT or your own authority.';
    if (metaDesc) metaDesc.setAttribute('content', content);
    refreshCaptcha();
  }, []);

  const refreshCaptcha = async () => {
    try {
      const { data } = await axios.get(`${API}/waitlist/captcha`);
      setCaptcha(data); setCaptchaAnswer('');
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('refreshCaptcha failed', err);
    }
  };

  const update = (patch) => setForm(prev => ({ ...prev, ...patch }));

  const onPickFiles = (incoming) => {
    const list = Array.from(incoming);
    const merged = [...files];
    for (const f of list) {
      if (merged.length >= MAX_PAPERWORK) { toast({ title: `Maximum ${MAX_PAPERWORK} paperwork files` }); break; }
      const ext = '.' + (f.name.split('.').pop() || '').toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) { toast({ title: 'File type not allowed', description: 'PDF, DOC, DOCX, JPG, PNG only.' }); continue; }
      if (f.size > MAX_BYTES) { toast({ title: 'File too large', description: `${f.name} exceeds 10MB.` }); continue; }
      merged.push(f);
    }
    setFiles(merged.slice(0, MAX_PAPERWORK));
  };
  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const onPickImage = (incoming, setter, label) => {
    const f = (incoming && incoming[0]) || null;
    if (!f) return;
    const ext = '.' + (f.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_IMG_EXT.includes(ext)) { toast({ title: `${label}: image only`, description: 'JPG, PNG or WEBP.' }); return; }
    if (f.size > MAX_BYTES) { toast({ title: 'File too large', description: `${f.name} exceeds 10MB.` }); return; }
    setter(f);
  };

  const scrollToForm = () => document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth' });

  const submit = async (e) => {
    e.preventDefault();
    // Required checks
    const req = ['firstName', 'lastName', 'phone', 'email', 'location', 'applyingAs', 'authorityType', 'equipmentType', 'experienceYears', 'startDate'];
    for (const k of req) {
      if (!form[k]) return toast({ title: 'Please complete all required fields', description: `Missing: ${k}` });
    }
    if (form.authorityType === 'own' && (!form.companyName || !form.mcNumber || !form.dotNumber)) {
      return toast({ title: 'Complete your authority details', description: 'Company, MC and DOT are required.' });
    }
    if (form.hasTrailer === 'yes' && (!form.trailerType || !form.trailerYear)) {
      return toast({ title: 'Complete trailer details', description: 'Trailer type and year are required.' });
    }
    if (files.length < 1) return toast({ title: 'Please upload at least 1 paperwork file', description: 'CDL, Insurance, Registration, Resume, W9 or other paperwork.' });
    if (!truckPhoto) return toast({ title: 'Please upload a photo of your truck' });
    if (form.hasTrailer === 'yes' && !trailerPhoto) return toast({ title: 'Please upload a photo of your trailer' });
    if (!captcha || captchaAnswer === '') return toast({ title: 'Please answer the security question' });
    // Source
    if (!form.source) return toast({ title: 'Please tell us where you heard about us' });
    if (form.source === 'Friend / Referral' && (!form.referralName || !form.referralPhone)) {
      return toast({ title: 'Please provide your friend\u2019s name and phone' });
    }
    if ((form.source === 'Existing Driver' || form.source === 'Existing Employee') && !form.referralName) {
      return toast({ title: `Please provide the ${form.source === 'Existing Driver' ? 'driver' : 'employee'} name` });
    }
    if (form.source === 'Other' && !form.otherSource) {
      return toast({ title: 'Please specify your source' });
    }

    setLoading(true);
    try {
      const fd = new FormData();
      [
        'firstName', 'lastName', 'phone', 'email', 'location', 'applyingAs', 'authorityType',
        'equipmentType', 'experienceYears', 'startDate'
      ].forEach(k => fd.append(k, form[k]));
      fd.append('hasTrailer', form.hasTrailer === 'yes');
      fd.append('cleanRecord', form.cleanRecord === 'yes');
      if (form.authorityType === 'own') {
        fd.append('companyName', form.companyName);
        fd.append('mcNumber', form.mcNumber);
        fd.append('dotNumber', form.dotNumber);
      }
      if (form.hasTrailer === 'yes') {
        fd.append('trailerType', form.trailerType);
        fd.append('trailerYear', form.trailerYear);
        if (form.trailerLength) fd.append('trailerLength', form.trailerLength);
        if (form.trailerType === 'Car Hauler' && form.carsCapacity) fd.append('carsCapacity', form.carsCapacity);
      }
      fd.append('hp', hp);
      fd.append('captchaA', captcha.a);
      fd.append('captchaB', captcha.b);
      fd.append('captchaAnswer', captchaAnswer);
      fd.append('captchaToken', captcha.token);
      // Source
      fd.append('source', form.source);
      if (form.referralName) fd.append('referralName', form.referralName);
      if (form.referralPhone) fd.append('referralPhone', form.referralPhone);
      if (form.otherSource) fd.append('otherSource', form.otherSource);
      files.forEach(f => fd.append('files', f));
      if (truckPhoto) fd.append('truckPhoto', truckPhoto);
      if (trailerPhoto) fd.append('trailerPhoto', trailerPhoto);

      await axios.post(`${API}/drivers`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDone(true);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('driver submit failed', err);
      toast({ title: 'Submission failed', description: err?.response?.data?.detail || 'Please check fields and try again.' });
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const showOwnAuthority = form.authorityType === 'own';
  const showTrailerFields = form.hasTrailer === 'yes';
  const showCarsCapacity = showTrailerFields && form.trailerType === 'Car Hauler';

  return (
    <>
      <Header />
      {!done && (
        <button onClick={scrollToForm} className="fixed bottom-6 right-6 z-40 btn-accent font-bold px-5 py-3 rounded-full shadow-2xl flex items-center gap-2 md:hidden">
          <Truck className="w-4 h-4" /> Apply Now
        </button>
      )}

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-[#0A1628] text-white">
          <div className="absolute inset-0 z-0 brand-gradient-tech" />
          <div className="absolute inset-0 z-0 circuit-bg-dark opacity-50" />
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#2AA8E8]/20 blur-3xl z-0" />
          <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[#D4A24C]/15 blur-3xl z-0" />

          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2AA8E8]/10 border border-[#2AA8E8]/30 text-xs font-bold text-[#2AA8E8] mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Now hiring
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] max-w-4xl">
              Drive With <span className="text-[#2AA8E8]">Excelence</span> <span className="gold-text">Inc</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg md:text-xl text-white/80">
              Join our network of professional drivers and owner operators. Work under our MC/DOT or bring your own authority — we support both.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button onClick={scrollToForm} className="btn-accent h-12 px-8 font-bold">Apply Now</Button>
              <a href="/careers"><Button variant="outline" className="h-12 px-6 font-semibold border-white/20 text-white bg-white/5 hover:bg-white hover:text-[#0A1628]">Office roles</Button></a>
            </div>

            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Shield, label: 'Our MC/DOT', sub: 'MC1766140 · DOT4474028' },
                { icon: Truck, label: '20k+ loads', sub: 'Monthly network capacity' },
                { icon: Award, label: 'Top pay', sub: 'Competitive rates' },
                { icon: Building2, label: 'NY HQ', sub: 'Established 2024' },
              ].map(({ icon: Icon, label, sub }, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <Icon className="w-5 h-5 text-[#2AA8E8] mb-2" />
                  <div className="font-bold text-sm">{label}</div>
                  <div className="text-xs text-white/60">{sub}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Form */}
        <section id="apply-form" className="section bg-[#F7F9FC]">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            {done ? (
              <div className="bg-white rounded-3xl shadow-xl p-10 md:p-14 border border-[#E5EAF2] text-center">
                <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-12 h-12 text-green-600" />
                </div>
                <h2 className="text-3xl md:text-4xl font-extrabold navy-text mb-3">Application received</h2>
                <p className="text-[#4A5A72] text-lg max-w-xl mx-auto">
                  Thank you for applying to Excelence Inc. Our team will review your application and contact you soon.
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  <a href="/"><Button variant="outline" className="font-semibold">Back to home</Button></a>
                  <a href="/services"><Button className="btn-accent font-bold">Explore services</Button></a>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-3xl shadow-xl p-6 md:p-10 border border-[#E5EAF2]">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-11 h-11 rounded-xl bg-[#0A1628] flex items-center justify-center">
                    <Truck className="w-5 h-5 text-[#2AA8E8]" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-extrabold navy-text">Driver Application</h2>
                    <p className="text-sm text-[#4A5A72]">Fields marked with <span className="text-[#D4A24C]">*</span> are required.</p>
                  </div>
                </div>

                {/* Honeypot */}
                <div className="hidden" aria-hidden>
                  <input tabIndex={-1} autoComplete="off" value={hp} onChange={e => setHp(e.target.value)} />
                </div>

                {/* 1. Personal */}
                <SectionHead num="1" title="Personal information" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  <div>
                    <Label>First name <span className="text-[#D4A24C]">*</span></Label>
                    <Input className="mt-1.5 h-11" value={form.firstName} onChange={e => update({ firstName: e.target.value })} placeholder="John" required />
                  </div>
                  <div>
                    <Label>Last name <span className="text-[#D4A24C]">*</span></Label>
                    <Input className="mt-1.5 h-11" value={form.lastName} onChange={e => update({ lastName: e.target.value })} placeholder="Smith" required />
                  </div>
                  <div>
                    <Label>Phone <span className="text-[#D4A24C]">*</span></Label>
                    <div className="relative mt-1.5">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                      <Input type="tel" className="pl-9 h-11" value={form.phone} onChange={e => update({ phone: e.target.value })} placeholder="(555) 000-0000" required />
                    </div>
                  </div>
                  <div>
                    <Label>Email <span className="text-[#D4A24C]">*</span></Label>
                    <div className="relative mt-1.5">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                      <Input type="email" className="pl-9 h-11" value={form.email} onChange={e => update({ email: e.target.value })} placeholder="you@email.com" required />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Current city / state <span className="text-[#D4A24C]">*</span></Label>
                    <div className="relative mt-1.5">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                      <Input className="pl-9 h-11" value={form.location} onChange={e => update({ location: e.target.value })} placeholder="Brooklyn, NY" required />
                    </div>
                  </div>
                </div>

                {/* 2. Applying As */}
                <SectionHead num="2" title="Applying as" />
                <RadioGroup value={form.applyingAs} onValueChange={v => update({ applyingAs: v })} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
                  {APPLYING_AS.map(v => (
                    <label key={v} className={`border rounded-xl px-4 py-4 cursor-pointer flex items-center gap-3 transition-colors ${form.applyingAs === v ? 'border-[#2AA8E8] bg-[#EFF8FD]' : 'border-[#E5EAF2] hover:border-[#0A1628]/30'}`}>
                      <RadioGroupItem value={v} id={`role-${v}`} />
                      <span className="font-bold text-sm navy-text">{v}</span>
                    </label>
                  ))}
                </RadioGroup>

                {/* 3. Authority */}
                <SectionHead num="3" title="Authority type" subtitle="Do you want to work under our authority or your own?" />
                <RadioGroup value={form.authorityType} onValueChange={v => update({ authorityType: v })} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  {[
                    { v: 'excelence', label: 'Excelence Inc MC / DOT', sub: 'Work under our authority' },
                    { v: 'own', label: 'My Own MC / DOT', sub: 'Bring your own authority' },
                  ].map(o => (
                    <label key={o.v} className={`border rounded-xl px-4 py-4 cursor-pointer flex items-start gap-3 transition-colors ${form.authorityType === o.v ? 'border-[#D4A24C] bg-[#FFF8EC]' : 'border-[#E5EAF2] hover:border-[#0A1628]/30'}`}>
                      <RadioGroupItem value={o.v} id={`auth-${o.v}`} className="mt-1" />
                      <div>
                        <div className="font-bold text-sm navy-text">{o.label}</div>
                        <div className="text-xs text-[#4A5A72]">{o.sub}</div>
                      </div>
                    </label>
                  ))}
                </RadioGroup>

                {showOwnAuthority && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 p-4 bg-[#F7F9FC] rounded-xl border border-[#E5EAF2]">
                    <div className="md:col-span-3">
                      <Label>Company name <span className="text-[#D4A24C]">*</span></Label>
                      <Input className="mt-1.5 h-11" value={form.companyName} onChange={e => update({ companyName: e.target.value })} placeholder="Your company LLC" />
                    </div>
                    <div>
                      <Label>MC Number <span className="text-[#D4A24C]">*</span></Label>
                      <Input className="mt-1.5 h-11" value={form.mcNumber} onChange={e => update({ mcNumber: e.target.value })} placeholder="MC1234567" />
                    </div>
                    <div>
                      <Label>DOT Number <span className="text-[#D4A24C]">*</span></Label>
                      <Input className="mt-1.5 h-11" value={form.dotNumber} onChange={e => update({ dotNumber: e.target.value })} placeholder="DOT1234567" />
                    </div>
                  </div>
                )}

                {/* 4. Equipment */}
                <SectionHead num="4" title="Equipment type" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                  {EQUIPMENT_TYPES.map(eq => (
                    <button type="button" key={eq} onClick={() => update({ equipmentType: eq })}
                      className={`text-left border rounded-xl px-4 py-4 transition-all ${form.equipmentType === eq ? 'border-[#2AA8E8] bg-[#EFF8FD]' : 'border-[#E5EAF2] hover:border-[#0A1628]/30'}`}>
                      <Truck className={`w-5 h-5 mb-2 ${form.equipmentType === eq ? 'text-[#2AA8E8]' : 'text-[#4A5A72]'}`} />
                      <div className="font-bold text-sm navy-text">{eq}</div>
                    </button>
                  ))}
                </div>

                {/* 5. Trailer */}
                <SectionHead num="5" title="Trailer information" subtitle="Do you have a trailer?" />
                <RadioGroup value={form.hasTrailer} onValueChange={v => update({ hasTrailer: v })} className="grid grid-cols-2 gap-3 mb-6 max-w-xs">
                  {[['yes', 'Yes'], ['no', 'No']].map(([v, l]) => (
                    <label key={v} className={`border rounded-xl px-4 py-3 cursor-pointer flex items-center gap-2 transition-colors ${form.hasTrailer === v ? 'border-[#D4A24C] bg-[#FFF8EC]' : 'border-[#E5EAF2] hover:border-[#0A1628]/30'}`}>
                      <RadioGroupItem value={v} id={`ht-${v}`} />
                      <span className="font-bold text-sm navy-text">{l}</span>
                    </label>
                  ))}
                </RadioGroup>

                {showTrailerFields && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 p-4 bg-[#F7F9FC] rounded-xl border border-[#E5EAF2]">
                    <div className="md:col-span-3">
                      <Label>Trailer type <span className="text-[#D4A24C]">*</span></Label>
                      <Select value={form.trailerType} onValueChange={v => update({ trailerType: v, carsCapacity: v === 'Car Hauler' ? form.carsCapacity : '' })}>
                        <SelectTrigger className="mt-1.5 h-11"><SelectValue placeholder="Select trailer type" /></SelectTrigger>
                        <SelectContent>
                          {TRAILER_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Trailer year <span className="text-[#D4A24C]">*</span></Label>
                      <Input type="number" className="mt-1.5 h-11" value={form.trailerYear} onChange={e => update({ trailerYear: e.target.value })} placeholder="2020" />
                    </div>
                    <div>
                      <Label>Trailer length (ft)</Label>
                      <Input type="number" className="mt-1.5 h-11" value={form.trailerLength} onChange={e => update({ trailerLength: e.target.value })} placeholder="e.g. 53" />
                    </div>
                    {showCarsCapacity && (
                      <div>
                        <Label>Cars capacity <span className="text-[#D4A24C]">*</span></Label>
                        <Input type="number" className="mt-1.5 h-11" value={form.carsCapacity} onChange={e => update({ carsCapacity: e.target.value })} placeholder="e.g. 7" />
                      </div>
                    )}
                  </div>
                )}

                {/* 6. Experience */}
                <SectionHead num="6" title="Experience" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div>
                    <Label>Years driving <span className="text-[#D4A24C]">*</span></Label>
                    <Input type="number" className="mt-1.5 h-11" value={form.experienceYears} onChange={e => update({ experienceYears: e.target.value })} placeholder="e.g. 5" required />
                  </div>
                  <div>
                    <Label>Clean driving record?</Label>
                    <RadioGroup value={form.cleanRecord} onValueChange={v => update({ cleanRecord: v })} className="grid grid-cols-2 gap-2 mt-1.5">
                      {[['yes', 'Yes'], ['no', 'No']].map(([v, l]) => (
                        <label key={v} className={`border rounded-lg px-3 py-2.5 cursor-pointer flex items-center gap-2 transition-colors ${form.cleanRecord === v ? 'border-[#D4A24C] bg-[#FFF8EC]' : 'border-[#E5EAF2] hover:border-[#0A1628]/30'}`}>
                          <RadioGroupItem value={v} id={`cr-${v}`} />
                          <span className="font-semibold text-sm navy-text">{l}</span>
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                  <div>
                    <Label>Available start date <span className="text-[#D4A24C]">*</span></Label>
                    <div className="relative mt-1.5">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                      <Input type="date" className="pl-9 h-11" value={form.startDate} onChange={e => update({ startDate: e.target.value })} required />
                    </div>
                  </div>
                </div>

                {/* 7. Source */}
                <SectionHead num="7" title="Where did you hear about us?" subtitle="This helps us understand which channels bring the best candidates." />
                <SourceSection
                  value={form.source}
                  referralName={form.referralName}
                  referralPhone={form.referralPhone}
                  otherSource={form.otherSource}
                  onChange={update}
                />

                {/* 8. Files */}
                <SectionHead num="8" title="Attachments" subtitle="Paperwork + photos — PDF/DOC/DOCX/JPG/PNG, 10MB each." />

                {/* 8a. Paperwork (multi-file) */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-bold navy-text">Paperwork · CDL · Insurance · Registration · Resume · W9 · Other</Label>
                    <span className="text-xs text-[#4A5A72]">{files.length}/{MAX_PAPERWORK} files</span>
                  </div>
                  <label
                    htmlFor="driver-file-input"
                    className="block cursor-pointer rounded-2xl border-2 border-dashed border-[#E5EAF2] hover:border-[#2AA8E8] hover:bg-[#F7F9FC] transition-colors p-6 text-center"
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); onPickFiles(e.dataTransfer.files); }}
                    data-testid="driver-paperwork-dropzone"
                  >
                    <Upload className="w-7 h-7 text-[#2AA8E8] mx-auto mb-2" />
                    <div className="font-bold navy-text text-sm">Click to upload or drag & drop paperwork</div>
                    <div className="text-xs text-[#4A5A72] mt-1">Up to {MAX_PAPERWORK} files · 10MB each · Add other paperwork as needed</div>
                    <input id="driver-file-input" data-testid="driver-paperwork-input" type="file" className="hidden" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => onPickFiles(e.target.files)} />
                  </label>
                  {files.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {files.map((f, i) => (
                        <li key={i} className="flex items-center gap-3 bg-[#F7F9FC] border border-[#E5EAF2] rounded-lg px-4 py-2.5">
                          <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center border border-[#E5EAF2]"><FileText className="w-4 h-4 text-[#2AA8E8]" /></div>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm navy-text truncate">{f.name}</div>
                            <div className="text-xs text-[#4A5A72]">{fmtBytes(f.size)}</div>
                          </div>
                          <button type="button" onClick={() => removeFile(i)} className="w-8 h-8 rounded-lg bg-white border border-[#E5EAF2] hover:border-red-300 hover:text-red-600 flex items-center justify-center"><X className="w-4 h-4" /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* 8b. Truck photo */}
                <div className="mb-6">
                  <Label className="text-sm font-bold navy-text block mb-2">
                    Truck photo <span className="text-red-500">*</span>
                    <span className="font-normal text-xs text-[#4A5A72] ml-2">1 image · JPG/PNG/WEBP · 10MB max</span>
                  </Label>
                  {!truckPhoto ? (
                    <label
                      htmlFor="driver-truck-input"
                      className="block cursor-pointer rounded-2xl border-2 border-dashed border-[#E5EAF2] hover:border-[#D4A24C] hover:bg-[#FFFBF0] transition-colors p-5 text-center"
                      data-testid="driver-truck-dropzone"
                    >
                      <Truck className="w-7 h-7 text-[#D4A24C] mx-auto mb-2" />
                      <div className="font-bold navy-text text-sm">Upload a clear photo of your truck</div>
                      <div className="text-xs text-[#4A5A72] mt-1">Side profile preferred — helps us verify equipment</div>
                      <input id="driver-truck-input" data-testid="driver-truck-input" type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp" onChange={e => onPickImage(e.target.files, setTruckPhoto, 'Truck photo')} />
                    </label>
                  ) : (
                    <div className="flex items-center gap-3 bg-[#FFFBF0] border border-[#D4A24C]/40 rounded-lg px-4 py-3" data-testid="driver-truck-preview">
                      <img src={URL.createObjectURL(truckPhoto)} alt="Truck preview" className="w-16 h-16 object-cover rounded-lg border border-[#E5EAF2]" />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm navy-text truncate">{truckPhoto.name}</div>
                        <div className="text-xs text-[#4A5A72]">{fmtBytes(truckPhoto.size)} · Truck photo</div>
                      </div>
                      <button type="button" onClick={() => setTruckPhoto(null)} data-testid="driver-truck-remove" className="w-8 h-8 rounded-lg bg-white border border-[#E5EAF2] hover:border-red-300 hover:text-red-600 flex items-center justify-center"><X className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>

                {/* 8c. Trailer photo (only when hasTrailer === yes) */}
                {form.hasTrailer === 'yes' && (
                  <div className="mb-6">
                    <Label className="text-sm font-bold navy-text block mb-2">
                      Trailer photo <span className="text-red-500">*</span>
                      <span className="font-normal text-xs text-[#4A5A72] ml-2">1 image · JPG/PNG/WEBP · 10MB max</span>
                    </Label>
                    {!trailerPhoto ? (
                      <label
                        htmlFor="driver-trailer-input"
                        className="block cursor-pointer rounded-2xl border-2 border-dashed border-[#E5EAF2] hover:border-[#D4A24C] hover:bg-[#FFFBF0] transition-colors p-5 text-center"
                        data-testid="driver-trailer-dropzone"
                      >
                        <Truck className="w-7 h-7 text-[#D4A24C] mx-auto mb-2" />
                        <div className="font-bold navy-text text-sm">Upload a clear photo of your trailer</div>
                        <div className="text-xs text-[#4A5A72] mt-1">Side profile preferred — helps dispatch match loads</div>
                        <input id="driver-trailer-input" data-testid="driver-trailer-input" type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp" onChange={e => onPickImage(e.target.files, setTrailerPhoto, 'Trailer photo')} />
                      </label>
                    ) : (
                      <div className="flex items-center gap-3 bg-[#FFFBF0] border border-[#D4A24C]/40 rounded-lg px-4 py-3" data-testid="driver-trailer-preview">
                        <img src={URL.createObjectURL(trailerPhoto)} alt="Trailer preview" className="w-16 h-16 object-cover rounded-lg border border-[#E5EAF2]" />
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm navy-text truncate">{trailerPhoto.name}</div>
                          <div className="text-xs text-[#4A5A72]">{fmtBytes(trailerPhoto.size)} · Trailer photo</div>
                        </div>
                        <button type="button" onClick={() => setTrailerPhoto(null)} data-testid="driver-trailer-remove" className="w-8 h-8 rounded-lg bg-white border border-[#E5EAF2] hover:border-red-300 hover:text-red-600 flex items-center justify-center"><X className="w-4 h-4" /></button>
                      </div>
                    )}
                  </div>
                )}

                {/* Captcha */}
                <div className="mt-8 bg-[#F7F9FC] rounded-xl p-4 border border-[#E5EAF2] flex items-center gap-4 flex-wrap">
                  <Shield className="w-5 h-5 text-[#2AA8E8] shrink-0" />
                  <div className="flex-1 min-w-[160px]">
                    <Label className="text-xs font-bold uppercase tracking-wider text-[#4A5A72]">Security check</Label>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="font-bold navy-text">What is {captcha ? captcha.question : '...'} ?</span>
                      <Input type="number" value={captchaAnswer} onChange={e => setCaptchaAnswer(e.target.value)} className="w-24 h-10" placeholder="?" />
                      <button type="button" onClick={refreshCaptcha} className="text-[#4A5A72] hover:text-[#0A1628]" aria-label="Refresh"><RefreshCw className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>

                <Button type="submit" disabled={loading} className="btn-primary mt-8 w-full h-12 text-base font-bold gap-2">
                  {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</> : <>Apply Now <Truck className="w-4 h-4" /></>}
                </Button>
                <p className="text-xs text-[#4A5A72] mt-4 text-center">By submitting, you agree to be contacted by Excelence Inc about this opportunity.</p>
              </form>
            )}
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-white py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="bg-[#0A1628] rounded-3xl p-8 md:p-14 text-white text-center relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#2AA8E8]/15 blur-2xl" />
              <div className="relative">
                <h3 className="text-3xl md:text-4xl font-extrabold leading-tight">Looking for office roles instead?</h3>
                <p className="mt-3 text-white/70 max-w-xl mx-auto">Check our logistics coordinators waitlist — perfect for dispatchers, freight coordinators and ops specialists.</p>
                <a href="/careers"><Button className="btn-accent mt-6 h-12 px-8 font-bold">Coordinator waitlist</Button></a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default DriversPage;
